import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { NonProfitManager } from '@ctct/application-layer';
import { isStripeBillingStrategy, type BillingStrategyQuery } from '@ctct/domain-layer';
import type { NonProfitHooks, NonProfitPageStore } from '@ctct/state-client-layer';
import { usePaymentPlanFacade, useUpdatePaymentPlan } from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';
import { useIsCoreMutationLockedAccountState } from '../helpers/useIsSingleBillChildAccount';

interface Props {
  initializer: Pick<NonProfitManager, 'nonProfitInit'>;
  pageStore: NonProfitPageStore;
  flags?: Parameters<NonProfitManager['nonProfitInit']>[0]['flags'];
  hooks: NonProfitHooks;
  billingStrategy: BillingStrategyQuery;
}

export function useNonProfitWidget({ initializer, pageStore, flags = [], hooks, billingStrategy }: Props) {
  const { t } = useTranslation();
  const isStripeUser = isStripeBillingStrategy(billingStrategy);
  const paymentPlan = usePaymentPlanFacade(isStripeUser);
  const updatePaymentPlanMutation = useUpdatePaymentPlan();

  const [skipInitializationFromUrl] = flags;
  const { isLocked: isCoreMutationLockedAccount, isPending: isCoreMutationLockPending } =
    useIsCoreMutationLockedAccountState({ billingStrategy });
  const isConfirmedCoreMutationLockedAccount = Boolean(isCoreMutationLockedAccount && !isCoreMutationLockPending);

  const componentProps = useMemo(
    () => ({
      ...initializer.nonProfitInit({
        pageStore,
        queries: [paymentPlan],
        mutations: [updatePaymentPlanMutation],
        flags: [skipInitializationFromUrl, isConfirmedCoreMutationLockedAccount, Boolean(isCoreMutationLockPending)],
        t,
      }),
      hooks,
      isSingleBillChildAccount: Boolean(isCoreMutationLockedAccount),
    }),
    [
      initializer,
      pageStore,
      paymentPlan,
      skipInitializationFromUrl,
      t,
      updatePaymentPlanMutation,
      hooks,
      isCoreMutationLockedAccount,
      isCoreMutationLockPending,
      isConfirmedCoreMutationLockedAccount,
    ]
  );

  useWidgetLifecycle(componentProps.status, componentProps.lifecycle, isCoreMutationLockPending);

  return componentProps;
}

export type NonProfitWidgetProps = ReturnType<typeof useNonProfitWidget>;
